import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  RotateCcw, ThumbsDown, Bookmark, ThumbsUp, Share, SlidersHorizontal,
  Map as MapIcon, LayoutGrid, ShieldCheck, MessagesSquare, FileText, Home, Flame,
} from "lucide-react";
import CardStack from "@/components/swipe/card_stack";
import NavTop from "@/components/nav/nav_top";
import FooterMain from "@/components/footer/footer_main";
import CardListing from "@/components/cards/card_listing";
import MatchModal from "@/components/swipe/match_modal";
import FilterSheet from "@/components/swipe/filter_sheet";
import ButtonIcon from "@/components/buttons/button_icon";
import ButtonGhost from "@/components/buttons/button_ghost";
import ButtonMain from "@/components/buttons/button_main";
import HeroCard from "@/components/landing/hero_card";
import RotatingHeadline from "@/components/landing/rotating_headline";
import ScrollHint from "@/components/landing/scroll_hint";
import ListingExpand from "@/components/swipe/listing_expand";
import SignupGate from "@/components/landing/signup_gate";
import CompleteProfileGate from "@/components/profile/complete_profile_gate";
import RenterSwipeDeck from "@/components/swipe/renter_swipe_deck";
import { CATEGORIES } from "@/components/landing/categories";
import CategoryBar from "@/components/landing/category_bar";
import { useAuth, userMode } from "@/context/AuthContext";
import { useMarket, matchesStay, marketFirst } from "@/context/LocationContext";
import { useFilters, inBudget, budgetParams } from "@/context/FiltersContext";
import { useSaves } from "@/context/SavesContext";
import api from "@/lib/api";
import { shareListing } from "@/lib/share";
import { toast } from "sonner";

function applyFilters(list, f, budget) {
  if (!f && !budget) return list;
  // WHY case-insensitive: the deck and the backend both match the city
  // case-insensitively ("coquitlam" is "Coquitlam"); an exact === here was
  // silently dropping homes the deck showed.
  const sameCity = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  return list.filter((l) => {
    if (!inBudget(l, budget)) return false;
    if (!f) return true;
    if (f.city && f.city !== "any" && !sameCity(l.city, f.city)) return false;
    if (f.minPrice && l.price < Number(f.minPrice)) return false;
    if (f.maxPrice && l.price > Number(f.maxPrice)) return false;
    if (f.beds && f.beds !== "any" && l.beds < Number(f.beds)) return false;
    // WHY explicit false only: a listing that merely LACKS the optional
    // field is unknown, not a "no" — the deck never drops those, so the
    // landing filters must not either. pets falls back to pet_friendly,
    // the field name the backend actually stores.
    if (f.pets && (l.pets ?? l.pet_friendly) === false) return false;
    if (f.furnished && l.furnished === false) return false;
    return true;
  });
}

const VALUES = [
  { icon: ShieldCheck, title: "Verified listers", text: "Every checkmark is a lister we screened, so you swipe on real homes only." },
  { icon: MessagesSquare, title: "Match and chat", text: "Like a home and talk to the lister the moment you both match." },
  { icon: FileText, title: "Lease it in app", text: "Screening, signing and deposits all happen in one place." },
];

// The landing page IS the swipe: one giant full-bleed card fills the first
// viewport on desktop, with the next two cards peeking from both sides.
export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { market, stayType, setStayType, needsPicker, setPickerOpen } = useMarket();
  const { budget } = useFilters();
  // Only true visitors are guests. Demo sessions are signed in users on an
  // offline backend, and every other swipe surface already lets them swipe.
  const isGuest = !user;
  // First-visit intro hints (swipe preview nudge, scroll cue, info-icon signal
  // ripple) show ONLY to logged-out visitors, and only until they've swiped
  // once (remembered so a returning guest isn't nagged every visit).
  const [introSeen, setIntroSeen] = useState(() => {
    try { return window.localStorage.getItem("sr_intro_seen") === "1"; } catch { return false; }
  });
  const showIntro = isGuest && !introSeen;
  // FIX 3 — in LISTER mode the landing hero deck shows RENTER cards (the
  // reverse deck) instead of listings, matching the /swipe deck.
  const listerMode = userMode(user) === "lister";
  // Home is a full-screen SWIPE hero only for guests (a marketing teaser) and
  // for listers (their reverse renter deck). A signed-in RENTER instead gets a
  // browse hub here — discovery + a "Swipe now" button — and does the real
  // swiping on the dedicated /swipe page (one swipe surface, no duplicate deck).
  const showSwipeHero = isGuest || listerMode;

  // The landing is pre-auth, so the hero deck loads REAL active listings from
  // the public listings feed. There is NO mock fallback — when the API returns
  // an empty list (or is unreachable) the hero shows an honest placeholder
  // instead of fabricated homes. `all` holds the fetched real listings; the
  // swipe deck (`cards`) starts from a fresh copy of it.
  const [all, setAll] = useState([]);
  const [cards, setCards] = useState([]);
  const [history, setHistory] = useState([]);
  // { id, dir } — a restored card reverse-swipes in from its exit vector.
  const [undone, setUndone] = useState(null);
  const [match, setMatch] = useState(null);
  const [filters, setFilters] = useState({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [cat, setCat] = useState("all");
  const [gateOpen, setGateOpen] = useState(false);
  const [profileGateOpen, setProfileGateOpen] = useState(false);
  // The Tinder-style profile expand: { listing, photo } while open. The
  // listing is snapshotted at open time so a deck advance never swaps the
  // popup content mid collapse.
  const [expand, setExpand] = useState(null);
  const gridRef = useRef(null);
  // Persistent stack handle: buttons, arrow keys and the popup all swipe the
  // live top card through it (same fly-out as a real drag).
  const stackRef = useRef(null);
  const { ids: savedIds, toggle: toggleSave } = useSaves();

  // Market first, stay type aware: the chosen market's homes lead the deck.
  const deck = useMemo(
    () => marketFirst(applyFilters(cards, filters, budget).filter((l) => matchesStay(l, stayType)), market?.city),
    [cards, filters, budget, stayType, market]
  );
  const top = deck[0];
  const activeFilterCount = Object.values(filters).filter((v) => v && v !== "any").length;

  const activeCat = CATEGORIES.find((c) => c.key === cat) || CATEGORIES[0];
  const gridListings = useMemo(
    () =>
      marketFirst(
        all.filter(
          (l) => inBudget(l, budget) && activeCat.test(l) && (cat === "short" || matchesStay(l, stayType))
        ),
        market?.city
      ),
    [all, activeCat, cat, stayType, market, budget]
  );

  // Load REAL active listings from the public feed. No mock fallback: an empty
  // or failed response leaves the deck empty so the honest placeholder shows,
  // never fabricated homes. The deck resets to the freshly fetched set.
  // Geographic fallback: when the visitor's city has no listings yet (e.g.
  // Burnaby before launch there), refetch WITHOUT the city filter so real
  // homes from everywhere else still show — marketFirst keeps any local
  // matches on top, and nothing is ever fabricated.
  useEffect(() => {
    let alive = true;
    const params = { stay_type: stayType, ...budgetParams(budget) };
    const unwrap = (res) => {
      const data = Array.isArray(res.data) ? res.data : res.data?.listings || res.data?.items;
      return Array.isArray(data) ? data : [];
    };
    const apply = (real) => {
      if (!alive) return;
      setAll(real);
      setCards(real.slice());
      setHistory([]);
    };
    // WHY 5: deck parity. The swipe deck (GET /swipes/deck) broadens beyond
    // the selected city whenever the city has fewer than 5 active listings
    // (backend PROVINCE_FILL_THRESHOLD), not only when it is EMPTY. The old
    // zero-only fallback here meant homes that appeared in the deck never
    // reached the landing feed while the city had 1 to 4 listings.
    const SPARSE_CITY = 5;
    api
      .get("/listings", { params: { ...params, city: market?.city } })
      .then((res) => {
        const real = unwrap(res);
        if (real.length >= SPARSE_CITY || !market?.city) return apply(real);
        // City sparse or empty: top up from everywhere (still real listings
        // only). City homes stay first; the top-up is deduped by id.
        return api.get("/listings", { params }).then((r2) => {
          const seen = new Set(real.map((l) => l.id));
          apply([...real, ...unwrap(r2).filter((l) => !seen.has(l.id))]);
        });
      })
      .catch(() => apply([]));
    return () => { alive = false; };
  }, [stayType, market?.city, budget]);

  // Category chips double as the stay type switch.
  const pickCat = useCallback(
    (key) => {
      if (key === "short") setStayType("short");
      else if (cat === "short") setStayType("long");
      setCat(key);
    },
    [cat, setStayType]
  );

  // Visitor outside Canada with no saved market: one time picker on landing.
  const promptedRef = useRef(false);
  useEffect(() => {
    if (needsPicker && !promptedRef.current) {
      promptedRef.current = true;
      setPickerOpen(true);
    }
  }, [needsPicker, setPickerOpen]);

  // Guests browse for free, but every swipe attempt opens the signup gate
  // instead of completing. Signed in users with an incomplete profile get the
  // finish-your-profile gate instead. Returns true when the action was blocked.
  const guard = useCallback(() => {
    if (isGuest) {
      setGateOpen(true);
      return true;
    }
    if (user?.profile_complete === false) {
      setProfileGateOpen(true);
      return true;
    }
    return false;
  }, [isGuest, user?.profile_complete]);

  // Called by the physics hook at fly-out START (optimistic). Returning false
  // vetoes the fly-out: the card springs back while the signup or profile
  // gate opens, so modals never freeze the card mid air.
  const swipe = useCallback(
    (action, listing) => {
      if (!listing) return false;
      if (guard()) return false;
      setUndone(null);
      setHistory((h) => [...h, { listing, action }]);
      setCards((c) => c.filter((x) => x.id !== listing.id));
      // Signed in swipes are REAL: persist them exactly like the /swipe deck
      // so likes on the landing deck are never silently lost. Guests never
      // reach this point, guard() already opened the signup gate for them.
      api
        .post("/swipes", { target_id: listing.id, target_type: "listing", action })
        .then((res) => {
          // A real server side match celebrates through the existing modal.
          if (res.data?.match) setMatch({ listing, matchId: res.data.match_id || res.data.match?.id || "m1" });
        })
        .catch((error) => {
          // Backend can refuse the swipe until the profile is complete:
          // restore the card and open the finish-your-profile gate.
          if (error?.response?.status === 403 && error.response.data?.code === "PROFILE_INCOMPLETE") {
            setCards((c) => (c.some((x) => x.id === listing.id) ? c : [listing, ...c]));
            setUndone({ id: listing.id, dir: action === "pass" ? -1 : 1 });
            setProfileGateOpen(true);
            return;
          }
          if (error?.response) return;
          // Pretend matches are DEMO ONLY: an offline demo session still
          // celebrates on some likes so the flow is demoable, but a real
          // user must never see a match the server did not create.
          // Deferred past the fly-out so the modal never interrupts it.
          if (user?.demo && action !== "pass" && Math.random() < 0.35) {
            setTimeout(() => setMatch({ listing, matchId: "m1" }), 380);
          }
        });
      return true;
    },
    [guard, user?.demo]
  );

  const undo = useCallback(() => {
    if (guard()) return;
    setHistory((h) => {
      if (!h.length) return h;
      const last = h[h.length - 1];
      setCards((c) => (c.some((x) => x.id === last.listing.id) ? c : [last.listing, ...c]));
      setUndone({ id: last.listing.id, dir: last.action === "pass" ? -1 : 1 });
      // Remove the swipe on the server too, so a withdrawn like does not
      // survive a refresh or stay visible to the lister. Demo has no backend.
      if (!user?.demo && last.listing?.id) {
        api.delete(`/swipes/listing/${last.listing.id}`).catch(() => {});
      }
      return h.slice(0, -1);
    });
  }, [guard, user?.demo]);

  // Buttons and arrow keys run the exact same fly-out as a drag via the
  // persistent stack handle. Returns false when the signup / profile gate
  // vetoed the swipe (the expand popup needs to know).
  const swipeTop = useCallback((action) => stackRef.current?.swipeTop(action) ?? false, []);

  // Live top-card id, readable from DELAYED callbacks (the expand popup
  // collapses first and fires its action ~200ms later, when render-time
  // closures may already be one deck advance stale).
  const topIdRef = useRef(null);
  useEffect(() => {
    topIdRef.current = deck[0]?.id ?? null;
  }, [deck]);

  // The expand popup must act on THE LISTING IT SHOWS. If the deck advanced
  // while the popup was open (or during its collapse), swiping the new top
  // card would like the wrong home, so we no-op with an honest toast instead.
  const expandAction = useCallback(
    (action, shown) => {
      if (!shown || topIdRef.current !== shown.id) {
        toast.info("The deck moved on, so nothing was swiped.");
        return false;
      }
      return swipeTop(action);
    },
    [swipeTop]
  );

  // The bookmark SAVES the home — it never advances the deck. Guests hit the
  // signup gate instead.
  const saveTop = useCallback(
    (listing) => {
      const target = listing || deck[0];
      if (!target) return;
      if (isGuest) {
        setGateOpen(true);
        return;
      }
      toggleSave(target);
    },
    [deck, isGuest, toggleSave]
  );

  // Arrow keys: left pass, right like, up toggles save (never advances).
  // Also paused while the expand popup is open so arrows never swipe the
  // deck hidden behind the popup (the popup acts on the listing it shows).
  useEffect(() => {
    function onKey(e) {
      if (match || sheetOpen || gateOpen || expand || !top) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "ArrowRight") { e.preventDefault(); swipeTop("like"); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); swipeTop("pass"); }
      else if (e.key === "ArrowUp") { e.preventDefault(); saveTop(top); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [top, swipeTop, saveTop, match, sheetOpen, gateOpen, expand]);

  const hintGone = history.length > 0;
  // Once they've swiped, retire the intro hints for good.
  useEffect(() => {
    if (hintGone && !introSeen) {
      setIntroSeen(true);
      try { window.localStorage.setItem("sr_intro_seen", "1"); } catch { /* storage blocked */ }
    }
  }, [hintGone, introSeen]);

  // Touch devices never see the arrow-keys mention in the first-visit hint.
  const [isTouch] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)")?.matches
  );

  return (
    <div className="min-h-screen bg-night pb-nav-safe md:pb-0">
      <NavTop />

      {/* ---- Immersive deck hero. Mobile: fills the first viewport (minus the
           top nav) so the swipe card is the clean, sole focus and the category
           row + browse grid drop BELOW the fold, only seen on scroll. Desktop:
           the richer landscape layout, unchanged. ---- */}
      <section className={`flex flex-col ${showSwipeHero ? "min-h-[calc(100dvh-5rem)] lg:min-h-0 lg:h-[calc(100vh-5rem)]" : ""}`}>
        {/* Slim header line — keeps the card tall. On mobile it stacks: a
            compact rotating headline, then a horizontally scrollable pill row.
            Kept tight so the card stays the clear focus above the fold. */}
        <div className="mx-auto w-full max-w-[1360px] shrink-0 px-4 pt-3 sm:px-6 lg:flex lg:h-12 lg:items-center lg:justify-between lg:gap-3 lg:px-10 lg:pt-0">
          <RotatingHeadline className="font-semibold leading-tight tracking-tight text-night-text text-[22px] lg:truncate lg:text-xl lg:leading-7" />
          <div className="no-scrollbar -mx-4 mt-2 flex items-center gap-2 overflow-x-auto px-4 lg:mx-0 lg:mt-0 lg:shrink-0 lg:overflow-visible lg:px-0">
            <Link
              to="/swipe"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-primary px-4 py-2.5 text-xs font-bold text-white transition hover:bg-brand-primary-hover lg:px-3.5 lg:py-1.5"
            >
              <Flame size={13} /> Swipe now
            </Link>
            <Link
              to="/map"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-night-line bg-night-surface px-4 py-2.5 text-xs font-semibold text-night-sub transition hover:border-brand-primary hover:text-night-text lg:px-3.5 lg:py-1.5"
            >
              <MapIcon size={13} /> Map view
            </Link>
            <button
              type="button"
              onClick={() => gridRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-night-line bg-night-surface px-4 py-2.5 text-xs font-semibold text-night-sub transition hover:border-brand-primary hover:text-night-text lg:px-3.5 lg:py-1.5"
            >
              <LayoutGrid size={13} /> Browse grid
            </button>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-night-line bg-night-surface px-4 py-2.5 text-xs font-semibold text-night-sub transition hover:border-brand-primary hover:text-night-text lg:px-3.5 lg:py-1.5"
            >
              <SlidersHorizontal size={13} /> Filters
              {activeFilterCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-primary text-[9px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* The deck itself. Mobile: one tall card (~58dvh) with the action row
            STRADDLING its bottom edge — trimmed a touch from the old 62dvh so
            the card no longer dominates the viewport and there is a clear gap
            between the straddling buttons and the bottom tab bar. Desktop keeps
            the giant landscape card exactly. */}
        <div className="flex min-h-0 flex-1 flex-col justify-center px-4 pb-14 pt-3 lg:flex-row lg:items-center lg:pb-12 lg:pt-2">
          {listerMode ? (
            /* FIX 3 — lister mode: the hero shows the RENTER deck (its own
               Tinder physics + like/pass/super buttons live inside it). */
            <div className="mx-auto w-full max-w-[430px]">
              <RenterSwipeDeck />
            </div>
          ) : !isGuest ? (
            /* Signed-in renter: Home is a browse hub, not a second swipe deck.
               Real swiping lives on /swipe — send them there with a clear CTA,
               and the Airbnb-style grid below is the discovery surface. */
            <div className="mx-auto flex w-full max-w-2xl flex-col items-center justify-center gap-5 py-10 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-night-tint text-brand-primary">
                <Flame size={26} />
              </span>
              <div>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-night-text sm:text-3xl">
                  Ready to find your next home?
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-night-sub">
                  Swipe through homes picked for you on the swipe page, or browse every listing in the grid below.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <ButtonMain onClick={() => navigate("/swipe")}>
                  <span className="inline-flex items-center gap-2">
                    <Flame size={16} /> Swipe now
                  </span>
                </ButtonMain>
                <ButtonGhost onClick={() => gridRef.current?.scrollIntoView({ behavior: "smooth" })}>
                  Browse all homes
                </ButtonGhost>
              </div>
            </div>
          ) : (
          <div className="relative mx-auto h-[58dvh] w-full max-w-[430px] lg:aspect-auto lg:h-[min(72vh,780px)] lg:max-h-full lg:w-[min(72vw,1150px)] lg:max-w-none">
            {deck.length === 0 ? (
              all.length === 0 ? (
                /* HONEST empty state — there are no real active listings yet.
                   The swipe UI and branding stay intact, but nothing here is a
                   fabricated home: no fake price, address or listing photo. */
                <div className="flex h-full flex-col items-center justify-center gap-5 rounded-3xl border border-dashed border-night-line bg-night-surface p-8 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-night-tint text-brand-primary shadow-sm">
                    <Home size={28} strokeWidth={1.7} />
                  </span>
                  <div>
                    <h3 className="font-display text-2xl font-semibold leading-snug text-night-text">
                      New homes are landing soon
                    </h3>
                    <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-night-sub">
                      Listers are just getting started. Sign up and we will let you swipe the moment the first homes go live.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <ButtonGhost onClick={() => navigate("/auth/signup")}>Create your account</ButtonGhost>
                    <ButtonGhost onClick={() => navigate("/map")}>Open the map</ButtonGhost>
                  </div>
                </div>
              ) : (
                /* The visitor swiped through every REAL home — offer a restart
                   over the real set, not any mock data. */
                <div className="flex h-full flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-night-line bg-night-surface p-8 text-center">
                  <h3 className="font-display text-2xl font-semibold leading-snug text-night-text">
                    You have seen every home
                  </h3>
                  <p className="text-sm text-night-sub">Start over, loosen your filters, or explore the map.</p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <ButtonGhost onClick={() => { setCards(all.slice()); setHistory([]); }}>Start over</ButtonGhost>
                    <ButtonGhost onClick={() => navigate("/map")}>Open the map</ButtonGhost>
                  </div>
                </div>
              )
            ) : (
              /* Persistent card shuffle: the window of 3 cards is keyed by
                 listing id and never remounts on advance — the committed card
                 finishes its fly-out on top while the next card is already
                 live underneath. */
              <CardStack
                ref={stackRef}
                deck={deck}
                onSwipe={swipe}
                undone={undone}
                demoPreview={showIntro}
                pose={(depth, lg) =>
                  depth === 0
                    ? lg
                      ? { x: 28, rotate: 1.6 }
                      : {}
                    : depth === 1
                    ? lg
                      ? { x: -28, rotate: -1.6 }
                      : { y: 14 }
                    : lg
                    ? { x: 28, rotate: 1.6 }
                    : { y: 28 }
                }
                renderCard={(l, props) => (
                  <HeroCard
                    listing={l}
                    onExpand={(photo) => {
                      setExpand({ listing: l, photo });
                      // Tapping the info icon retires the signal ripple for good.
                      setIntroSeen(true);
                      try { window.localStorage.setItem("sr_intro_seen", "1"); } catch { /* storage blocked */ }
                    }}
                    hint={showIntro}
                    {...props}
                  />
                )}
              />
            )}

            {/* First visit hint — fades after the first swipe. Sits below the
                progress dots and clear of the info button (top-16, not top-10). */}
            {showIntro && deck.length > 0 && (
              <div
                className={`pointer-events-none absolute inset-x-0 top-16 z-10 flex justify-center transition-opacity duration-700 ${
                  hintGone ? "opacity-0" : "opacity-100"
                }`}
              >
                <span className="rounded-full bg-black/40 px-3.5 py-1.5 text-[11px] font-medium text-white">
                  {isTouch ? "Drag the card to like or pass" : "Drag the card or press the arrow keys"}
                </span>
              </div>
            )}

            {/* Desktop floating action row — overlaps the card's bottom edge,
                truly centered relative to the card; buttons run the same
                fly-out as a drag via the card ref */}
            <div
              className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 hidden translate-y-1/2 items-center justify-center gap-3 lg:flex ${
                top ? "" : "opacity-0"
              }`}
            >
              <ButtonIcon icon={RotateCcw} label="Undo last swipe" onClick={undo} size={18} className="pointer-events-auto bg-white border-transparent text-amber-500 shadow-[0_6px_16px_rgba(30,42,56,0.12)] h-12 w-12" />
              <ButtonIcon icon={ThumbsDown} label="Pass" onClick={() => swipeTop("pass")} size={28} className="pointer-events-auto bg-white border-transparent text-pass shadow-[0_10px_24px_rgba(30,42,56,0.16)] h-16 w-16" />
              <ButtonIcon
                icon={Bookmark}
                label="Save this home"
                aria-pressed={!!top && savedIds.has(top.id)}
                onClick={() => saveTop(top)}
                size={20}
                className="pointer-events-auto bg-white border-transparent text-brand-primary shadow-[0_6px_16px_rgba(30,42,56,0.12)] h-12 w-12"
                iconClassName={top && savedIds.has(top.id) ? "fill-brand-primary" : ""}
              />
              <ButtonIcon icon={ThumbsUp} label="Like" onClick={() => swipeTop("like")} size={28} className="pointer-events-auto bg-white border-transparent text-like shadow-[0_10px_24px_rgba(30,42,56,0.16)] h-16 w-16" />
              <ButtonIcon icon={Share} label="Share this home" onClick={() => shareListing(top)} size={18} className="pointer-events-auto bg-white border-transparent text-brand-ink shadow-[0_6px_16px_rgba(30,42,56,0.12)] h-12 w-12" />
            </div>

            {/* Mobile action row — STRADDLES the card's bottom edge (classic
                Tinder): bottom-0 + translateY 50% centers the row on the edge,
                upper halves over the photo, lower halves onto the page.
                pointer-events on the buttons only so the photo stays draggable /
                tappable underneath. */}
            <div
              className={`pointer-events-none absolute inset-x-0 bottom-0 z-20 flex translate-y-1/2 items-center justify-center gap-3 lg:hidden ${
                top ? "" : "opacity-0"
              }`}
            >
              <ButtonIcon icon={RotateCcw} label="Undo last swipe" onClick={undo} size={18} className="pointer-events-auto bg-white border-transparent text-amber-500 shadow-[0_6px_16px_rgba(30,42,56,0.12)] h-12 w-12" />
              <ButtonIcon icon={ThumbsDown} label="Pass" onClick={() => swipeTop("pass")} size={26} className="pointer-events-auto bg-white border-transparent text-pass shadow-[0_8px_20px_rgba(30,42,56,0.14)] h-14 w-14" />
              <ButtonIcon
                icon={Bookmark}
                label="Save this home"
                aria-pressed={!!top && savedIds.has(top.id)}
                onClick={() => saveTop(top)}
                size={20}
                className="pointer-events-auto bg-white border-transparent text-brand-primary shadow-[0_6px_16px_rgba(30,42,56,0.12)] h-12 w-12"
                iconClassName={top && savedIds.has(top.id) ? "fill-brand-primary" : ""}
              />
              <ButtonIcon icon={ThumbsUp} label="Like" onClick={() => swipeTop("like")} size={26} className="pointer-events-auto bg-white border-transparent text-like shadow-[0_8px_20px_rgba(30,42,56,0.14)] h-14 w-14" />
              <ButtonIcon icon={Share} label="Share this home" onClick={() => shareListing(top)} size={18} className="pointer-events-auto bg-white border-transparent text-brand-ink shadow-[0_6px_16px_rgba(30,42,56,0.12)] h-12 w-12" />
            </div>
          </div>
          )}
        </div>
      </section>

      {/* ---- Below the fold: the classic Airbnb style grid ---- */}
      <section ref={gridRef} className="border-t border-night-line">
        <div className="bg-night">
          <CategoryBar value={cat} onPick={pickCat} />
        </div>

        <div className="mx-auto max-w-[1360px] px-4 pb-16 pt-8 sm:px-6 lg:px-10">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-night-text">Or browse the classic way</h2>
          <p className="mt-1 text-sm text-night-sub">
            {gridListings.length > 0
              ? `${gridListings.length} home${gridListings.length === 1 ? "" : "s"} across Canada, ${market?.city || "Toronto"} first`
              : "The first homes are on their way. Check back soon."}
          </p>
          {gridListings.length > 0 ? (
            <div className="mt-8 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {gridListings.map((l) => (
                <CardListing key={l.id} listing={l} onClick={() => navigate(`/listing/${l.id}`)} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-night-line bg-night-surface px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-night-tint text-brand-primary shadow-sm">
                <Home size={24} strokeWidth={1.7} />
              </span>
              <p className="max-w-sm text-sm leading-relaxed text-night-sub">
                No homes are listed just yet. Sign up and you will be first to swipe when they land.
              </p>
              <ButtonGhost onClick={() => navigate("/auth/signup")}>Create your account</ButtonGhost>
            </div>
          )}
        </div>
      </section>

      {/* Value row */}
      <section className="border-t border-night-line">
        <div className="mx-auto grid max-w-[1360px] gap-8 px-4 py-12 sm:grid-cols-3 sm:px-6 lg:px-10">
          {VALUES.map((v) => (
            <div key={v.title} className="flex items-start gap-3.5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-night-tint text-brand-primary">
                <v.icon size={20} strokeWidth={1.8} />
              </span>
              <div>
                <h3 className="text-[15px] font-semibold text-night-text">{v.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-night-sub">{v.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <FooterMain />

      {/* One-shot scroll-down teach — fades in after the swipe preview, tells
          mobile visitors the categories + grid live below the fold. */}
      {showIntro && <ScrollHint />}

      {/* Tinder-style profile expand — likes and nopes inside it run the same
          deck swipe; guests still hit the signup gate (mounted before the
          modals so gates paint above the popup) */}
      <ListingExpand
        open={!!expand}
        listing={expand?.listing}
        initialPhoto={expand?.photo || 0}
        onClose={() => setExpand(null)}
        onAction={expandAction}
        onRequireAuth={() => setGateOpen(true)}
      />

      <FilterSheet open={sheetOpen} onClose={() => setSheetOpen(false)} filters={filters} onApply={setFilters} />
      <MatchModal open={!!match} listing={match?.listing} matchId={match?.matchId} onClose={() => setMatch(null)} />
      <SignupGate open={gateOpen} onClose={() => setGateOpen(false)} />
      <CompleteProfileGate
        open={profileGateOpen}
        onClose={() => setProfileGateOpen(false)}
        missing={user?.missing_fields || []}
        role={user?.role}
      />
    </div>
  );
}
